/**
 * Email reply recipients (To + CC) — the composer-side rules. The BFF and
 * ticket-service enforce the same limits; checking here lets the agent fix a
 * typo before anything is sent.
 */

/** Per-field cap, mirrored by the BFF DTO and ticket-service. */
export const MAX_REPLY_RECIPIENTS = 20;

// Practical address check: dot-atom local part (no leading, trailing or double
// dots) and a hostname with an alphabetic TLD. Deliberately no quoted locals or
// IP-literal domains — the backend validator rejects those as well.
const LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

export function isValidEmail(address: string): boolean {
  if (address.length > 254) return false;
  const at = address.lastIndexOf('@');
  if (at <= 0 || at === address.length - 1) return false;
  const local = address.slice(0, at);
  const domain = address.slice(at + 1);
  return local.length <= 64 && LOCAL_PART.test(local) && DOMAIN.test(domain);
}

/** Lowercase + trim; also unwraps a pasted `Name <addr>` into `addr`. */
export function normalizeAddress(raw: string): string {
  const trimmed = raw.trim();
  const angle = trimmed.match(/<([^<>]+)>\s*$/);
  return (angle ? angle[1] : trimmed).trim().toLowerCase();
}

/** Split typed or pasted text into individual addresses (`,` `;` or whitespace). */
export function splitAddresses(text: string): string[] {
  // Split on , and ; first so a "Name <addr>" entry stays whole (its name may
  // contain spaces); entries without angle brackets may still be
  // whitespace-separated lists. Order is preserved.
  return text
    .split(/[,;]+/)
    .flatMap((segment) => (/<[^<>]+>/.test(segment) ? [segment] : segment.split(/\s+/)))
    .map(normalizeAddress)
    .filter((a) => a.length > 0);
}

/** Only email conversations send to addresses, so only they get To/Cc. */
export function supportsReplyRecipients(conversation: { channel?: string | null } | null | undefined): boolean {
  return conversation?.channel?.toLowerCase() === 'email';
}

/**
 * The `to`/`cc` part of the send-message request. Empty for internal notes and
 * when the composer had no recipient fields — the backend then replies to the
 * ticket contact, exactly as before. An empty Cc is left out.
 */
export function recipientsPayload(
  isInternal: boolean,
  recipients: { to: string[]; cc: string[] } | undefined,
): { to?: string[]; cc?: string[] } {
  if (isInternal || !recipients) return {};
  if (recipients.to.length === 0 && recipients.cc.length === 0) return {};
  return {
    to: recipients.to,
    ...(recipients.cc.length > 0 ? { cc: recipients.cc } : {}),
  };
}

/**
 * Why the current recipients can't be sent, or null when they can. Messages
 * are user-facing.
 */
export function getRecipientError(
  to: string[],
  cc: string[],
  { requireTo = true }: { requireTo?: boolean } = {},
): string | null {
  // An email ticket always needs a To. Elsewhere the rows are optional, but a
  // Cc on its own is still not a sendable email.
  if (to.length === 0 && (requireTo || cc.length > 0)) return 'Add at least one recipient in the To field.';
  const invalid = [...to, ...cc].filter((a) => !isValidEmail(a));
  if (invalid.length === 1) return `"${invalid[0]}" is not a valid email address.`;
  if (invalid.length > 1) {
    return `${invalid.length} email addresses are not valid: ${invalid.map((a) => `"${a}"`).join(', ')}.`;
  }
  if (to.length > MAX_REPLY_RECIPIENTS) return `To can have at most ${MAX_REPLY_RECIPIENTS} recipients.`;
  if (cc.length > MAX_REPLY_RECIPIENTS) return `Cc can have at most ${MAX_REPLY_RECIPIENTS} recipients.`;
  return null;
}
